import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tag, Plus, Pencil, Trash2, ListPlus, Eye, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = () => ({
  name: "", description: "", is_default: false, is_active: true,
  client_id: null, company_id: null, client_tag: "",
  fallback_discount_pct: 0, valid_from: "", valid_until: "",
});

export default function PriceLists() {
  const [lists, setLists] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [goods, setGoods] = useState([]);
  const [edit, setEdit] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [activeList, setActiveList] = useState(null);
  const [items, setItems] = useState([]);
  const [itemDialog, setItemDialog] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const [l, c, s, g] = await Promise.all([
        axios.get(`${API}/price-lists?include_inactive=true`),
        axios.get(`${API}/clients`).catch(() => ({ data: [] })),
        axios.get(`${API}/services`).catch(() => ({ data: [] })),
        axios.get(`${API}/goods`).catch(() => ({ data: [] })),
      ]);
      setLists(l.data || []);
      setClients(c.data?.items || c.data || []);
      setServices(s.data?.items || s.data || []);
      setGoods(g.data?.items || g.data || []);
    } catch { toast.error("Failed to load price lists"); }
  };
  useEffect(() => { load(); }, []);

  const loadItems = async (listId) => {
    try { const r = await axios.get(`${API}/price-lists/${listId}/items`); setItems(r.data || []); }
    catch { toast.error("Items load failed"); }
  };

  const openList = (l) => { setActiveList(l); loadItems(l.id); };

  const saveList = async () => {
    try {
      if (edit.id) await axios.put(`${API}/price-lists/${edit.id}`, edit);
      else await axios.post(`${API}/price-lists`, edit);
      toast.success("Saved"); setEditOpen(false); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const removeList = async (id) => {
    if (!window.confirm("Deactivate price list?")) return;
    try { await axios.delete(`${API}/price-lists/${id}`); load(); if (activeList?.id === id) setActiveList(null); }
    catch { toast.error("Failed"); }
  };

  const saveItem = async () => {
    if (!itemDialog?.kind || !itemDialog?.item_id) { toast.error("Pick kind + item"); return; }
    try {
      const body = {
        kind: itemDialog.kind, item_id: itemDialog.item_id,
        override_price: itemDialog.mode === "override" ? parseFloat(itemDialog.override_price || 0) : null,
        discount_pct: itemDialog.mode === "discount" ? parseFloat(itemDialog.discount_pct || 0) : null,
        notes: itemDialog.notes || null,
      };
      if (itemDialog.id) await axios.put(`${API}/price-lists/${activeList.id}/items/${itemDialog.id}`, body);
      else await axios.post(`${API}/price-lists/${activeList.id}/items`, body);
      toast.success("Item saved"); setItemDialog(null); loadItems(activeList.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Item save failed"); }
  };

  const removeItem = async (rowId) => {
    if (!window.confirm("Remove this override?")) return;
    try { await axios.delete(`${API}/price-lists/${activeList.id}/items/${rowId}`); loadItems(activeList.id); }
    catch { toast.error("Delete failed"); }
  };

  const runPreview = async () => {
    try { const r = await axios.get(`${API}/price-lists/${activeList.id}/preview`); setPreview(r.data); }
    catch { toast.error("Preview failed"); }
  };

  const catalogPool = useMemo(() => ([
    ...services.map((s) => ({ value: `service:${s.id}`, kind: "service", id: s.id, label: `🛠 ${s.name} ($${s.default_price || 0})` })),
    ...goods.map((g) => ({ value: `good:${g.id}`, kind: "good", id: g.id, label: `📦 ${g.name} ($${g.unit_price || 0})` })),
  ]), [services, goods]);

  return (
    <div className="space-y-6 animate-in" data-testid="price-lists-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Tag className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Price Lists</h1>
          <p className="text-muted-foreground">Tiered or customer-specific pricing for services + goods.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LISTS PANEL */}
        <Card>
          <CardHeader className="flex flex-row justify-between items-center">
            <div><CardTitle>{lists.length} lists</CardTitle><CardDescription>Default applies to clients with no list assigned.</CardDescription></div>
            <Button onClick={() => { setEdit(blank()); setEditOpen(true); }} data-testid="new-price-list-btn"><Plus className="h-4 w-4 mr-1" /> New list</Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Scope</TableHead><TableHead>Fallback %</TableHead><TableHead>Status</TableHead><TableHead className="text-right" />
              </TableRow></TableHeader>
              <TableBody>
                {lists.map((l) => (
                  <TableRow key={l.id} className={`cursor-pointer ${activeList?.id === l.id ? "bg-primary/5" : ""} ${!l.is_active ? "opacity-50" : ""}`} onClick={() => openList(l)} data-testid={`price-list-row-${l.id}`}>
                    <TableCell className="font-medium">{l.name} {l.is_default && <Badge className="ml-1">Default</Badge>}</TableCell>
                    <TableCell className="text-xs">{l.client_id ? "client-specific" : l.company_id ? "company" : l.client_tag ? `tag: ${l.client_tag}` : "tenant-wide"}</TableCell>
                    <TableCell>{l.fallback_discount_pct ? `${l.fallback_discount_pct}%` : "—"}</TableCell>
                    <TableCell>{l.is_active ? <Badge variant="outline">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEdit({ ...l }); setEditOpen(true); }} data-testid={`edit-list-${l.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={(e) => { e.stopPropagation(); removeList(l.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {lists.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No price lists yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* ITEMS PANEL */}
        <Card>
          <CardHeader className="flex flex-row justify-between items-center">
            <div>
              <CardTitle>{activeList ? `Items in "${activeList.name}"` : "Pick a list"}</CardTitle>
              <CardDescription>{activeList ? "Override prices for individual services/goods." : "Click a list on the left to manage its items."}</CardDescription>
            </div>
            {activeList && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={runPreview} data-testid="preview-btn"><Eye className="h-4 w-4 mr-1" />Preview</Button>
                <Button size="sm" onClick={() => setItemDialog({ kind: "service", item_id: "", mode: "discount", override_price: "", discount_pct: "", notes: "" })} data-testid="add-item-btn"><ListPlus className="h-4 w-4 mr-1" />Add item</Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {activeList ? (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Kind</TableHead><TableHead>Item</TableHead><TableHead>Catalog</TableHead><TableHead>Override</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} data-testid={`pl-item-${it.id}`}>
                      <TableCell><Badge variant="outline">{it.kind}</Badge></TableCell>
                      <TableCell className="font-medium">{it.item_name}</TableCell>
                      <TableCell>{it.catalog_price !== null ? `$${it.catalog_price}` : "—"}</TableCell>
                      <TableCell>{it.override_price !== null ? `$${it.override_price}` : it.discount_pct !== null ? `${it.discount_pct}% off` : "—"}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setItemDialog({
                          ...it, mode: it.override_price !== null ? "override" : "discount",
                        })}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeItem(it.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No item overrides yet. {activeList.fallback_discount_pct > 0 && <>Fallback <b>{activeList.fallback_discount_pct}%</b> applies to all catalog items.</>}</TableCell></TableRow>}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground py-8 text-center">No list selected.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* List editor */}
      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEdit(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit list" : "New price list"}</DialogTitle><DialogDescription>Tiered pricing for a tag, company, or single client.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="list-name-input" /></div>
                <div><Label>Fallback discount %</Label><Input type="number" step="0.1" value={edit.fallback_discount_pct} onChange={(e) => setEdit({ ...edit, fallback_discount_pct: parseFloat(e.target.value || 0) })} data-testid="fallback-pct-input" /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Bind to client (optional)</Label>
                  <Select value={edit.client_id || "_none"} onValueChange={(v) => setEdit({ ...edit, client_id: v === "_none" ? null : v })}>
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Any client</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || `${c.first_name} ${c.last_name}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Or client tag</Label><Input value={edit.client_tag || ""} onChange={(e) => setEdit({ ...edit, client_tag: e.target.value })} placeholder="vip / trade" /></div>
                <label className="flex flex-col cursor-pointer pt-6">
                  <span className="text-sm">Default tier</span>
                  <Switch checked={edit.is_default} onCheckedChange={(v) => setEdit({ ...edit, is_default: v })} data-testid="is-default-switch" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Valid from</Label><Input type="date" value={edit.valid_from || ""} onChange={(e) => setEdit({ ...edit, valid_from: e.target.value })} /></div>
                <div><Label>Valid until</Label><Input type="date" value={edit.valid_until || ""} onChange={(e) => setEdit({ ...edit, valid_until: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveList} data-testid="save-list-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item editor */}
      <Dialog open={!!itemDialog} onOpenChange={(o) => !o && setItemDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{itemDialog?.id ? "Edit override" : "Add item override"}</DialogTitle></DialogHeader>
          {itemDialog && (
            <div className="space-y-3">
              {!itemDialog.id && (
                <div>
                  <Label>Catalog item</Label>
                  <Select value={`${itemDialog.kind}:${itemDialog.item_id}`} onValueChange={(v) => {
                    const [kind, id] = v.split(":");
                    setItemDialog({ ...itemDialog, kind, item_id: id });
                  }}>
                    <SelectTrigger data-testid="item-picker"><SelectValue placeholder="Pick a service or good" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {catalogPool.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Pricing mode</Label>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => setItemDialog({ ...itemDialog, mode: "discount" })} className={`flex-1 px-3 py-2 rounded border text-sm ${itemDialog.mode === "discount" ? "border-primary bg-primary/10" : ""}`} data-testid="mode-discount">% Discount</button>
                  <button onClick={() => setItemDialog({ ...itemDialog, mode: "override" })} className={`flex-1 px-3 py-2 rounded border text-sm ${itemDialog.mode === "override" ? "border-primary bg-primary/10" : ""}`} data-testid="mode-override">Absolute $</button>
                </div>
              </div>
              {itemDialog.mode === "discount" ? (
                <div><Label>Discount %</Label><Input type="number" step="0.1" value={itemDialog.discount_pct || ""} onChange={(e) => setItemDialog({ ...itemDialog, discount_pct: e.target.value })} data-testid="discount-input" /></div>
              ) : (
                <div><Label>Override price ($)</Label><Input type="number" step="0.01" value={itemDialog.override_price || ""} onChange={(e) => setItemDialog({ ...itemDialog, override_price: e.target.value })} data-testid="override-input" /></div>
              )}
              <div><Label>Notes</Label><Textarea rows={2} value={itemDialog.notes || ""} onChange={(e) => setItemDialog({ ...itemDialog, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(null)}>Cancel</Button>
            <Button onClick={saveItem} data-testid="save-item-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview drawer */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle>Impact preview</DialogTitle><DialogDescription>Every catalog item × this list&apos;s effective price.</DialogDescription></DialogHeader>
          {preview && (
            <Table>
              <TableHeader><TableRow><TableHead>Kind</TableHead><TableHead>Item</TableHead><TableHead>Catalog</TableHead><TableHead>Effective</TableHead><TableHead>Override?</TableHead></TableRow></TableHeader>
              <TableBody>
                {preview.map((r) => (
                  <TableRow key={`${r.kind}:${r.id}`}>
                    <TableCell><Badge variant="outline">{r.kind}</Badge></TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.catalog !== null ? `$${r.catalog}` : "—"}</TableCell>
                    <TableCell className="font-medium">{r.effective !== null ? `$${r.effective}` : "—"}</TableCell>
                    <TableCell>{r.has_override ? <Sparkles className="h-4 w-4 text-primary" /> : null}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
